import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { LoanExtension } from '@/types';

// Custom endpoint — keep manual
export function useExtensions(loanId: string) {
  return useQuery<LoanExtension[]>({
    queryKey: ['extensions', loanId],
    queryFn: async () => {
      const { data } = await api.get(`/loans/${loanId}/extensions`);
      return data;
    },
    enabled: !!loanId,
  });
}

export interface ExtensionSummary {
  totalExtensions: number;
  totalAdditionalMonths: number;
  totalExtensionFees: number;
  totalExtensionFeesRetained: number;
  totalExtensionFeesPaidOut: number;
  extensions: LoanExtension[];
}

// Custom endpoint /summary — keep manual
export function useExtensionSummary(loanId: string) {
  return useQuery<ExtensionSummary>({
    queryKey: ['extensions', loanId, 'summary'],
    queryFn: async () => {
      const { data } = await api.get(`/loans/${loanId}/extensions/summary`);
      return data;
    },
    enabled: !!loanId,
  });
}

export interface ExtendLoanInput {
  additionalMonths: number;
  newMaturityDate?: string;
  extensionFee?: number;
  extensionFeeRetained?: number;
  extensionFeePaidOut?: number;
  newInterestRate?: number;
  reason?: string;
  notes?: string;
}

// Custom endpoint /extensions (POST) with complex invalidation — keep manual
export function useExtendLoan(loanId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: ExtendLoanInput) => {
      const { data } = await api.post(`/loans/${loanId}/extensions`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loans', loanId] });
      qc.invalidateQueries({ queryKey: ['extensions', loanId] });
      qc.invalidateQueries({ queryKey: ['deal-analysis', loanId] });
      qc.invalidateQueries({ queryKey: ['credit-pack', loanId] });
      qc.invalidateQueries({ queryKey: ['loans'] });
    },
  });
}
